import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import PersianDatePicker from '../components/PersianDatePicker';
import { HIS_MENU_TITLE } from '../config/hisSections';
import { canAccessHisReport } from '../utils/hisReportAccess';
import { formatRial } from '../utils/formatMoneyFa';
import { getApiBaseUrl } from '../utils/getApiUrl';

type DatePreset = 'today' | 'month' | 'last7' | 'custom';
type VoidFilter = 'all' | 'cancelled_service' | 'deleted_reception' | 'deleted_dowork';

interface ReceptionVoidItem {
  kind: 'cancelled_service' | 'deleted_reception';
  record_code: string;
  patient_name: string;
  age?: string;
  operator_name: string;
  recept_at: string;
  event_at: string;
  doctor_name?: string;
  service_label: string;
  service_name?: string;
  service_amount?: number;
  modality: string;
  insurance_name: string;
  insu_takmili_name?: string;
  national_id?: string;
  page_no?: string;
  serial_no?: string;
  script_date?: string;
  action?: string;
  note?: string;
  registrar_name?: string;
  reception_exists: boolean;
  database?: string;
}

interface DoworkVoidItem {
  log_id?: number;
  record_code: string;
  patient_name: string;
  image_type: string;
  image_name: string;
  routin_name: string;
  dowork_at: string;
  specialist: string;
  deleted_by: string;
  deleted_at: string;
  action?: string;
  database?: string;
}

type DetailState =
  | { type: 'reception'; row: ReceptionVoidItem }
  | { type: 'dowork'; row: DoworkVoidItem }
  | null;

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const DetailField: React.FC<{ label: string; value?: string | number | null; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => {
  const text =
    value === null || value === undefined || value === ''
      ? '—'
      : typeof value === 'number'
        ? toPersianDigits(value)
        : toPersianDigits(value);
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className={`mt-0.5 text-sm font-medium text-slate-900 ${mono ? 'tabular-nums' : ''}`}>
        {text}
      </p>
    </div>
  );
};

interface VoidReport {
  from_date: string;
  to_date: string;
  cancelled_service_count: number;
  deleted_reception_count: number;
  deleted_dowork_count: number;
  reception_items: ReceptionVoidItem[];
  dowork_items: DoworkVoidItem[];
}

const DATE_PRESETS: { id: DatePreset; label: string; apply: () => { from: string; to: string } }[] = [
  {
    id: 'today',
    label: 'امروز',
    apply: () => {
      const d = moment().format('jYYYY/jMM/jDD');
      return { from: d, to: d };
    },
  },
  {
    id: 'month',
    label: 'ماه جاری',
    apply: () => ({
      from: moment().startOf('jMonth').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
  {
    id: 'last7',
    label: '۷ روز اخیر',
    apply: () => ({
      from: moment().subtract(6, 'day').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
];

const FILTERS: { id: VoidFilter; label: string }[] = [
  { id: 'all', label: 'همه' },
  { id: 'cancelled_service', label: 'لغو خدمت' },
  { id: 'deleted_reception', label: 'پذیرش پاک‌شده' },
  { id: 'deleted_dowork', label: 'انجام‌کار پاک‌شده' },
];

type SortDir = 'asc' | 'desc';

type ReceptionSortKey =
  | 'kind'
  | 'record_code'
  | 'patient_name'
  | 'operator_name'
  | 'recept_at'
  | 'event_at'
  | 'service_label'
  | 'modality';

type DoworkSortKey =
  | 'record_code'
  | 'patient_name'
  | 'image_type'
  | 'image_name'
  | 'routin_name'
  | 'specialist'
  | 'dowork_at'
  | 'deleted_by'
  | 'deleted_at';

const compareSortValues = (a: unknown, b: unknown): number => {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  const na = Number(sa.replace(/[^\d.-]/g, ''));
  const nb = Number(sb.replace(/[^\d.-]/g, ''));
  const bothNumeric =
    sa.replace(/[^\d.-]/g, '') !== '' &&
    sb.replace(/[^\d.-]/g, '') !== '' &&
    !Number.isNaN(na) &&
    !Number.isNaN(nb) &&
    /[\d]/.test(sa) &&
    /[\d]/.test(sb);
  // تاریخ شمسی YYYY/MM/DD با مقایسه رشته‌ای درست مرتب می‌شود
  if (bothNumeric && !sa.includes('/') && !sb.includes('/')) {
    return na - nb;
  }
  return sa.localeCompare(sb, 'fa', { numeric: true, sensitivity: 'base' });
};

const SortTh: React.FC<{
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}> = ({ label, active, dir, onClick }) => (
  <th className="px-3 py-2 font-medium">
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded px-0.5 hover:text-indigo-700 ${
        active ? 'text-indigo-700' : 'text-slate-500'
      }`}
      title="کلیک برای مرتب‌سازی"
    >
      <span>{label}</span>
      <span className="text-[10px] tabular-nums opacity-80">
        {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  </th>
);

const AdminHisVoids: React.FC = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(() => moment().startOf('jMonth').format('jYYYY/jMM/jDD'));
  const [toDate, setToDate] = useState(() => moment().format('jYYYY/jMM/jDD'));
  const [datePreset, setDatePreset] = useState<DatePreset>('month');
  const [filter, setFilter] = useState<VoidFilter>('all');
  const [report, setReport] = useState<VoidReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>(null);
  const [receptionSort, setReceptionSort] = useState<{ key: ReceptionSortKey; dir: SortDir }>({
    key: 'event_at',
    dir: 'desc',
  });
  const [doworkSort, setDoworkSort] = useState<{ key: DoworkSortKey; dir: SortDir }>({
    key: 'deleted_at',
    dir: 'desc',
  });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessHisReport()) navigate('/admin/dashboard');
  }, [navigate]);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDetail(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detail]);

  const applyDatePreset = (preset: DatePreset) => {
    const found = DATE_PRESETS.find((item) => item.id === preset);
    if (!found) return;
    const next = found.apply();
    setFromDate(next.from);
    setToDate(next.to);
    setDatePreset(preset);
    setReport(null);
  };

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      setError('بازه تاریخ را کامل کنید');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      const res = await fetch(`${getApiBaseUrl()}/admin/reports/his/voids?${params}`, {
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت گزارش');
      }
      setReport(body as VoidReport);
    } catch (err: unknown) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'خطا در دریافت گزارش');
    } finally {
      setLoading(false);
    }
  };

  const toggleReceptionSort = (key: ReceptionSortKey) => {
    setReceptionSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const toggleDoworkSort = (key: DoworkSortKey) => {
    setDoworkSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const receptionRows = useMemo(() => {
    let rows = report?.reception_items || [];
    if (filter === 'deleted_dowork') return [];
    if (filter !== 'all') rows = rows.filter((row) => row.kind === filter);
    const { key, dir } = receptionSort;
    const sorted = [...rows].sort((a, b) => {
      let av: unknown = a[key];
      let bv: unknown = b[key];
      if (key === 'kind') {
        av = a.kind === 'deleted_reception' ? 'پذیرش پاک‌شده' : 'لغو خدمت';
        bv = b.kind === 'deleted_reception' ? 'پذیرش پاک‌شده' : 'لغو خدمت';
      }
      if (key === 'modality') {
        av = a.modality || a.insurance_name;
        bv = b.modality || b.insurance_name;
      }
      const cmp = compareSortValues(av, bv);
      return dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filter, report, receptionSort]);

  const doworkRows = useMemo(() => {
    if (!report) return [];
    if (filter === 'cancelled_service' || filter === 'deleted_reception') return [];
    const { key, dir } = doworkSort;
    return [...report.dowork_items].sort((a, b) => {
      const cmp = compareSortValues(a[key], b[key]);
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [filter, report, doworkSort]);

  return (
    <div className="w-full space-y-4">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-indigo-600 via-indigo-700 to-slate-800 px-5 py-4 text-white shadow-lg shadow-indigo-900/20 sm:px-6">
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate('/admin/his')}
              className="mb-1 text-[11px] text-indigo-200 hover:text-white"
            >
              ← بازگشت به {HIS_MENU_TITLE}
            </button>
            <p className="text-xs font-medium uppercase tracking-wider text-indigo-200">لاگ HIS</p>
            <h1 className="mt-0.5 text-xl font-bold tracking-tight sm:text-2xl">لغو و حذف</h1>
            <p className="mt-1 max-w-xl text-xs text-indigo-100/90">
              لغو خدمت روی پذیرشِ موجود، پذیرشی که از لیست پاک شده، و انجام‌کاری که پاک شده ولی مشخصاتش در لاگ مانده است.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm sm:p-2.5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyDatePreset(preset.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                    datePreset === preset.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50/60">
              <div className="w-[7.25rem] border-l border-slate-200">
                <PersianDatePicker
                  compact
                  borderless
                  value={fromDate}
                  placeholder="از"
                  onChange={(value) => {
                    setFromDate(value);
                    setDatePreset('custom');
                    setReport(null);
                  }}
                />
              </div>
              <div className="w-[7.25rem]">
                <PersianDatePicker
                  compact
                  borderless
                  value={toDate}
                  placeholder="تا"
                  onChange={(value) => {
                    setToDate(value);
                    setDatePreset('custom');
                    setReport(null);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 lg:mr-auto">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setFilter(item.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                    filter === item.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => void fetchReport()}
              disabled={loading}
              className="inline-flex h-8 items-center rounded-lg bg-indigo-600 px-3 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'در حال خواندن...' : 'اجرا'}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
      ) : null}

      {report ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => setFilter('cancelled_service')}
              className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3 text-right"
            >
              <p className="text-[11px] text-amber-800/80">لغو خدمت</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-amber-950">
                {report.cancelled_service_count.toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] text-amber-800/70">پذیرش هنوز در HIS هست</p>
            </button>
            <button
              type="button"
              onClick={() => setFilter('deleted_reception')}
              className="rounded-xl border border-rose-100 bg-rose-50/70 px-4 py-3 text-right"
            >
              <p className="text-[11px] text-rose-800/80">پذیرش پاک‌شده</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-rose-950">
                {report.deleted_reception_count.toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] text-rose-800/70">فقط در لاگ حذف مانده</p>
            </button>
            <button
              type="button"
              onClick={() => setFilter('deleted_dowork')}
              className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-right"
            >
              <p className="text-[11px] text-slate-500">انجام‌کار پاک‌شده</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                {report.deleted_dowork_count.toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">مشخصات در لاگ انجام‌کار مانده</p>
            </button>
          </div>

          {receptionRows.length ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">
                لغو و حذف پذیرش
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-right text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <SortTh
                        label="وضعیت"
                        active={receptionSort.key === 'kind'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('kind')}
                      />
                      <SortTh
                        label="کد پذیرش"
                        active={receptionSort.key === 'record_code'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('record_code')}
                      />
                      <SortTh
                        label="بیمار"
                        active={receptionSort.key === 'patient_name'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('patient_name')}
                      />
                      <SortTh
                        label="کاربر"
                        active={receptionSort.key === 'operator_name'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('operator_name')}
                      />
                      <SortTh
                        label="زمان پذیرش"
                        active={receptionSort.key === 'recept_at'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('recept_at')}
                      />
                      <SortTh
                        label="زمان حذف"
                        active={receptionSort.key === 'event_at'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('event_at')}
                      />
                      <SortTh
                        label="خدمت"
                        active={receptionSort.key === 'service_label'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('service_label')}
                      />
                      <SortTh
                        label="بخش"
                        active={receptionSort.key === 'modality'}
                        dir={receptionSort.dir}
                        onClick={() => toggleReceptionSort('modality')}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {receptionRows.map((row, index) => (
                      <tr
                        key={`${row.record_code}-${row.event_at}-${row.service_label}-${index}`}
                        className="cursor-pointer border-t border-slate-100 hover:bg-indigo-50/50"
                        onClick={() => setDetail({ type: 'reception', row })}
                        title="کلیک برای جزئیات"
                      >
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              row.kind === 'deleted_reception'
                                ? 'bg-rose-50 text-rose-700'
                                : 'bg-amber-50 text-amber-800'
                            }`}
                          >
                            {row.kind === 'deleted_reception' ? 'پذیرش پاک‌شده' : 'لغو خدمت'}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.record_code || '—')}</td>
                        <td className="px-3 py-2">{row.patient_name || '—'}</td>
                        <td className="px-3 py-2">{row.operator_name || '—'}</td>
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.recept_at || '—')}</td>
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.event_at || '—')}</td>
                        <td className="px-3 py-2">{row.service_label || '—'}</td>
                        <td className="px-3 py-2">{row.modality || row.insurance_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {doworkRows.length ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">
                انجام‌کار پاک‌شده
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-right text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <SortTh
                        label="کد پذیرش"
                        active={doworkSort.key === 'record_code'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('record_code')}
                      />
                      <SortTh
                        label="بیمار"
                        active={doworkSort.key === 'patient_name'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('patient_name')}
                      />
                      <SortTh
                        label="نوع"
                        active={doworkSort.key === 'image_type'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('image_type')}
                      />
                      <SortTh
                        label="تصویر"
                        active={doworkSort.key === 'image_name'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('image_name')}
                      />
                      <SortTh
                        label="روتین"
                        active={doworkSort.key === 'routin_name'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('routin_name')}
                      />
                      <SortTh
                        label="متخصص"
                        active={doworkSort.key === 'specialist'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('specialist')}
                      />
                      <SortTh
                        label="زمان انجام"
                        active={doworkSort.key === 'dowork_at'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('dowork_at')}
                      />
                      <SortTh
                        label="حذف توسط"
                        active={doworkSort.key === 'deleted_by'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('deleted_by')}
                      />
                      <SortTh
                        label="زمان حذف"
                        active={doworkSort.key === 'deleted_at'}
                        dir={doworkSort.dir}
                        onClick={() => toggleDoworkSort('deleted_at')}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {doworkRows.map((row, index) => (
                      <tr
                        key={`${row.record_code}-${row.deleted_at}-${index}`}
                        className="cursor-pointer border-t border-slate-100 hover:bg-indigo-50/50"
                        onClick={() => setDetail({ type: 'dowork', row })}
                        title="کلیک برای جزئیات"
                      >
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.record_code || '—')}</td>
                        <td className="px-3 py-2">{row.patient_name || '—'}</td>
                        <td className="px-3 py-2">{row.image_type || '—'}</td>
                        <td className="px-3 py-2">{row.image_name || '—'}</td>
                        <td className="px-3 py-2">{row.routin_name || '—'}</td>
                        <td className="px-3 py-2">{row.specialist || '—'}</td>
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.dowork_at || '—')}</td>
                        <td className="px-3 py-2">{row.deleted_by || '—'}</td>
                        <td className="px-3 py-2 tabular-nums">{toPersianDigits(row.deleted_at || '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {!receptionRows.length && !doworkRows.length ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
              در این بازه موردی با این فیلتر نیست.
            </div>
          ) : null}
        </div>
      ) : null}

      {detail
        ? createPortal(
            <div className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-6" dir="rtl">
              <button
                type="button"
                className="absolute inset-0 bg-slate-950/45"
                aria-label="بستن"
                onClick={() => setDetail(null)}
              />
              <div
                className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-indigo-100"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-start justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-indigo-50 to-white px-5 py-4">
                  <div>
                    <p className="text-xs text-indigo-700/80">جزئیات لغو / حذف</p>
                    <h3 className="mt-0.5 text-lg font-bold text-slate-900">
                      {detail.type === 'reception'
                        ? detail.row.patient_name || '—'
                        : detail.row.patient_name || 'انجام‌کار پاک‌شده'}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {detail.type === 'reception'
                        ? detail.row.kind === 'deleted_reception'
                          ? 'پذیرش پاک‌شده'
                          : 'لغو خدمت'
                        : 'انجام‌کار پاک‌شده'}
                      {detail.type === 'reception' && detail.row.record_code
                        ? ` · کد ${toPersianDigits(detail.row.record_code)}`
                        : detail.type === 'dowork' && detail.row.record_code
                          ? ` · کد ${toPersianDigits(detail.row.record_code)}`
                          : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDetail(null)}
                    className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
                  >
                    بستن
                  </button>
                </header>

                <div className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-4">
                  {detail.type === 'reception' ? (
                    <>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        <DetailField label="کد پذیرش" value={detail.row.record_code} mono />
                        <DetailField label="بیمار" value={detail.row.patient_name} />
                        <DetailField label="سن" value={detail.row.age} mono />
                        <DetailField label="کاربر حذف‌کننده" value={detail.row.operator_name} />
                        <DetailField label="ثبت‌کننده پذیرش" value={detail.row.registrar_name} />
                        <DetailField label="پزشک" value={detail.row.doctor_name} />
                        <DetailField label="زمان پذیرش" value={detail.row.recept_at} mono />
                        <DetailField label="زمان حذف" value={detail.row.event_at} mono />
                        <DetailField label="عملیات لاگ" value={detail.row.action || 'حذف'} />
                        <DetailField label="بخش / مودالیتی" value={detail.row.modality} />
                        <DetailField label="خدمت" value={detail.row.service_name || detail.row.service_label} />
                        <DetailField
                          label="مبلغ خدمت"
                          value={
                            detail.row.service_amount
                              ? formatRial(detail.row.service_amount)
                              : '—'
                          }
                          mono
                        />
                        <DetailField label="بیمه پایه" value={detail.row.insurance_name} />
                        <DetailField label="بیمه تکمیلی" value={detail.row.insu_takmili_name} />
                        <DetailField label="کد ملی / شناسه" value={detail.row.national_id} mono />
                        <DetailField label="شماره صفحه" value={detail.row.page_no} mono />
                        <DetailField label="سریال" value={detail.row.serial_no} mono />
                        <DetailField label="تاریخ نسخه" value={detail.row.script_date} mono />
                        <DetailField
                          label="وضعیت پذیرش در HIS"
                          value={detail.row.reception_exists ? 'هنوز موجود است' : 'از لیست پاک شده'}
                        />
                        <DetailField label="دیتابیس" value={detail.row.database} />
                      </div>
                      <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5">
                        <p className="text-[11px] font-medium text-amber-900">یادداشت پذیرش</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                          {detail.row.note?.trim()
                            ? detail.row.note
                            : detail.row.reception_exists
                              ? 'یادداشتی ثبت نشده'
                              : 'پذیرش پاک شده؛ یادداشت در لاگ موجود نیست'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <DetailField label="کد پذیرش" value={detail.row.record_code} mono />
                      <DetailField label="بیمار" value={detail.row.patient_name} />
                      <DetailField label="نوع تصویر" value={detail.row.image_type} />
                      <DetailField label="نام تصویر" value={detail.row.image_name} />
                      <DetailField label="روتین" value={detail.row.routin_name} />
                      <DetailField label="متخصص" value={detail.row.specialist} />
                      <DetailField label="زمان انجام‌کار" value={detail.row.dowork_at} mono />
                      <DetailField label="حذف توسط" value={detail.row.deleted_by} />
                      <DetailField label="زمان حذف" value={detail.row.deleted_at} mono />
                      <DetailField label="کد عملیات" value={detail.row.action || 'D'} />
                      <DetailField label="دیتابیس" value={detail.row.database} />
                      <DetailField label="شناسه لاگ" value={detail.row.log_id} mono />
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default AdminHisVoids;
